/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Theme resolution: get by id, default, CSS variables, apply to document.
 */

import { logger } from "@/lib/utils/logger";
import type { OrganizationTheme } from "./types";
import { allThemes, neutralGoldTheme, organizationThemes } from "./palettes";
import { applyThemeVars } from "@/components/providers/theme-provider/utils/theme-style-tag";

export function getDefaultTheme(): OrganizationTheme {
  return neutralGoldTheme;
}

export function getThemeById(themeId: string): OrganizationTheme | undefined {
  return organizationThemes[themeId];
}

export function getThemeOrDefault(themeId?: string | null): OrganizationTheme {
  if (!themeId) return getDefaultTheme();
  return getThemeById(themeId) ?? getDefaultTheme();
}

export function getThemeCSSVariables(
  themeId?: string | null,
): Record<string, string> {
  const theme = getThemeOrDefault(themeId);
  return theme.cssVariables;
}

export function applyTheme(themeId: string): void {
  const theme = getThemeById(themeId);
  if (!theme) {
    logger.warn(`Theme "${themeId}" not found, using default`);
    return applyTheme(getDefaultTheme().id);
  }

  const vars: Record<string, string> = {};
  Object.entries(theme.cssVariables).forEach(([property, value]) => {
    vars[property] = value;
  });
  applyThemeVars(vars);
}

export function getRecommendedThemes(industry: string): OrganizationTheme[] {
  return allThemes.filter((theme) =>
    theme.recommendedFor.some((rec) =>
      rec.toLowerCase().includes(industry.toLowerCase()),
    ),
  );
}

// Re-export for resolution.ts consumers that need the registry
export { organizationThemes, allThemes };
